import { Box, Button, Img, Stack, Text, useToast } from "@chakra-ui/react";
import React, { useRef, useState, useEffect } from "react";

import "./ImageUpload.css";
import { AiFillFile, AiFillVideoCamera } from "react-icons/ai";

// Size limits (bytes) - aligned with backend INSA security requirements
const SIZE_LIMITS = {
  image: 5 * 1024 * 1024,    // 5 MB
  video: 300 * 1024 * 1024,  // 300 MB
  pdf: 10 * 1024 * 1024,     // 10 MB
};

// Allowed extensions - whitelist per INSA
const ALLOWED_EXTENSIONS = {
  image: [".jpg", ".jpeg", ".png", ".gif", ".webp"],
  video: [".mp4", ".avi", ".mov", ".wmv", ".flv", ".mkv", ".webm", ".m3u8", ".ts"],
  pdf: [".pdf"],
};

const ImageUpload = (props) => {
  const { form, field, prevPic, htmlFor, fileSize, fileType } = props;
  const [file, setFile] = useState();
  const [count, setCount] = useState(0);
  const [validationError, setValidationError] = useState(null);
  const toast = useToast();

  const [previewUrl, setPreviewUrl] = useState(prevPic);
  const [isValid, setIsValid] = useState(false);
  const isPdf = fileType === "pdf" && htmlFor === "file";
  const isFile = htmlFor === "file" && fileType !== "pdf";
  const isVideo = htmlFor === "video";
  const isImage = htmlFor === "image" || htmlFor === "thumbnail" || htmlFor === "avatar";

  const filePickerRef = useRef();

  useEffect(() => {
    if (!file) {
      return;
    }
    const fileReader = new FileReader();
    // setFile(fileReader);
    form?.setFieldValue("", fileReader);
    fileReader.onload = () => {
      if (isPdf || isFile || isVideo) {
        setPreviewUrl("file")
      } else {
        setPreviewUrl(fileReader.result);
      }
    };
    fileReader.readAsDataURL(file);
  }, [file]);

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const validateFile = (pickedFile) => {
    setValidationError(null);
    const ext = "." + (pickedFile.name.split(".").pop() || "").toLowerCase();
    let limit, allowed;
    if (isPdf) {
      limit = SIZE_LIMITS.pdf;
      allowed = ALLOWED_EXTENSIONS.pdf;
    } else if (isVideo) {
      limit = SIZE_LIMITS.video;
      allowed = ALLOWED_EXTENSIONS.video;
    } else if (isImage) {
      limit = SIZE_LIMITS.image;
      allowed = ALLOWED_EXTENSIONS.image;
    } else {
      limit = SIZE_LIMITS.pdf;
      allowed = ALLOWED_EXTENSIONS.pdf;
    }
    if (!allowed.includes(ext)) {
      return `File type not allowed. Allowed: ${allowed.join(", ")}`;
    }
    if (pickedFile.size > limit) {
      const mb = Math.round(limit / (1024 * 1024));
      return `File size exceeds ${mb} MB limit`;
    }
    return null;
  };

  const pickedHandler = (event) => {
    if (!event.target.files || event.target.files.length !== 1) {
      setIsValid(false);
      setValidationError(null);
      return;
    }
    const pickedFile = event.target.files[0];
    const error = validateFile(pickedFile);
    if (error) {
      setValidationError(error);
      setIsValid(false);
      form?.setFieldValue(htmlFor, null);
      setFile(null);
      setCount(0);
      toast({
        title: error,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      event.target.value = "";
      return;
    }
    setValidationError(null);
    form?.setFieldValue(htmlFor, pickedFile);
    setFile(pickedFile);
    setIsValid(true);
    setCount(1);
    event.target.value = "";
  };

  const pickImageHandler = () => {
    filePickerRef.current.click();
  };

  return (
    <div className="form-control">
      <input
        id={props.id}
        ref={filePickerRef}
        style={{ display: "none" }}
        type="file"
        accept={isPdf ?
          ".pdf" :
          isFile ?
            ".pdf" :
            isVideo ? ".mp4,.avi,.mov,.wmv,.flv,.mkv,.webm,.m3u8,.ts" :
              isImage ? ".jpg,.jpeg,.png,.gif,.webp" : ""
        }
        onChange={pickedHandler}
      />
      <div className={`image-upload ${props.center && "center"}`}>
        <Stack direction="row" spacing="25px" align="center">
          <div className={`${isFile || isPdf || isVideo ? "file" : "image"}-upload__preview`}>
            {previewUrl && count === 0 && (
              <>
                <Stack>
                  <Box cursor="pointer">
                    {
                      isFile || isPdf ?
                        <AiFillFile
                          size={38}
                          color="#408ee1"
                          onClick={() => window.open(`${process.env.REACT_APP_FILE_URL}${prevPic}`, "_blank")}
                        />
                        : isVideo ?
                          <AiFillVideoCamera
                            size={38}
                            color="#408ee1"
                            onClick={() => window.open(`${process.env.REACT_APP_FILE_URL}${prevPic}`, "_blank")}
                          />
                          :
                          <Img
                            src={`${process.env.REACT_APP_FILE_URL}${previewUrl}`}
                            maxH={"12rem"}
                            //   rounded="full"
                            alt="Preview"
                          />
                    }
                  </Box>
                  <Text fontSize="14px">
                    {fileSize}
                  </Text>
                </Stack>
              </>
            )}
            {previewUrl && count > 0 && (
              <Stack minH={isFile || isPdf ? "70px" : isVideo ? "70px" : "180px"}>
                {
                  isFile || isPdf ?
                    <AiFillFile
                      size={38}
                      color="#408ee1"
                    /> :
                    isVideo ?
                      <AiFillVideoCamera
                        size={38}
                        color="#408ee1"
                      /> :
                      <Img maxH="180px" src={previewUrl} alt="Preview" />
                }
                <Text> {formatBytes(file?.size)} </Text>
              </Stack>
            )}
            {!previewUrl && (
              isPdf ? <p>{"Attach a PDF."}</p> :
                isFile ? <p>{"Attach a file."}</p> :
                  isVideo ? <p>{"Attach a video."}</p> :
                    isImage ? <p>{"Please pick an image."}</p> :
                      null
            )}
          </div>
          <Button type="button" onClick={pickImageHandler}>
            {isPdf ? "Pick PDF" : isFile ? "Pick File" : isVideo ? "Pick Video" : "Pick Image"}
          </Button>
        </Stack>
      </div>
      {(validationError || (!isValid && props.errorText)) && (
        <Text color="red.500" fontSize="sm" mt={1}>
          {validationError || props.errorText}
        </Text>
      )}
    </div>
  );
};

export default ImageUpload;
